import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Sidebar from "../../components/Sidebar"; // Ensure this path is correct
import Topbar from "../../components/Topbar";   // Ensure this path is correct
import { Modal, Button, Form, Table, Spinner, Alert, Container, Row, Col, Card, Badge } from "react-bootstrap"; // Added Card and Badge
import { FiPlusCircle, FiEdit, FiTrash2, FiEye, FiCalendar, FiUser, FiFileText } from "react-icons/fi"; // Added more icons
import toast, { Toaster } from "react-hot-toast";

// API base URL - adjust if your setup is different
const API_BASE_URL = `${process.env.REACT_APP_APIURL}api/blog`; // Assuming your app.js mounts blog routes here

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    console.error("Date formatting error:", error);
    return "Invalid Date";
  }
};

const Blogs = () => {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null); // For editing
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [blogToView, setBlogToView] = useState(null);


  const initialFormData = {
    title: "",
    author: "Team Booze247",
    excerpt: "",
    // content: "", // Add if 'content' is in your BlogModel
    date: new Date().toISOString().split("T")[0], // Default to today
  };
  const [formData, setFormData] = useState(initialFormData);

  // Fetch all blogs
  const {
    data: blogsData,
    isLoading: isLoadingBlogs,
    isError: isErrorBlogs,
    error: errorBlogs,
  } = useQuery({
    queryKey: ["blogs"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}`);
      if (response.data.status === true) {
        return response.data.data.sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
      }
      throw new Error(response.data.message || "Failed to fetch blogs");
    },
  });

  // Mutation for creating a blog
  const createBlogMutation = useMutation({
    mutationFn: async (newBlog) => {
      return axios.post(API_BASE_URL, newBlog);
    },
    onSuccess: (response) => {
      if (response.data.status === true) {
        toast.success("Blog created successfully!");
        queryClient.invalidateQueries({ queryKey: ["blogs"] });
        handleCloseModal();
      } else {
        // Try to get specific error from backend if available
        const message = response.data.errors ? response.data.errors.map(e => e.msg).join(', ') : (response.data.message || "Failed to create blog");
        throw new Error(message);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || "An error occurred during creation.");
    },
  });

  // Mutation for updating a blog
  const updateBlogMutation = useMutation({
    mutationFn: async (updatedBlog) => {
      return axios.put(`${API_BASE_URL}/${currentBlog._id}`, updatedBlog);
    },
    onSuccess: (response) => {
      if (response.data.status === true) {
        toast.success("Blog updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["blogs"] });
        handleCloseModal();
      } else {
        const message = response.data.errors ? response.data.errors.map(e => e.msg).join(', ') : (response.data.message || "Failed to update blog");
        throw new Error(message);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || "An error occurred during update.");
    },
  });

  // Mutation for deleting a blog
  const deleteBlogMutation = useMutation({
    mutationFn: async (blogId) => {
      return axios.delete(`${API_BASE_URL}/${blogId}`);
    },
    onSuccess: (response) => {
      if (response.data.status === true) {
        toast.success("Blog deleted successfully!");
        queryClient.invalidateQueries({ queryKey: ["blogs"] });
        handleCloseDeleteConfirm();
      } else {
        throw new Error(response.data.message || "Failed to delete blog");
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || "An error occurred during deletion.");
    },
  });

  const handleShowModal = (blog = null) => {
    if (blog) {
      setIsEditing(true);
      setCurrentBlog(blog);
      setFormData({
        title: blog.title || "",
        author: blog.author || "",
        excerpt: blog.excerpt || "",
        // content: blog.content || "", 
        date: blog.date ? new Date(blog.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      });
    } else {
      setIsEditing(false);
      setCurrentBlog(null);
      setFormData(initialFormData);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentBlog(null);
    setIsEditing(false);
    setFormData(initialFormData);
  };

  const handleViewBlog = (blog) => {
    setBlogToView(blog);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setBlogToView(null);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.author.trim() || !formData.excerpt.trim()) {
        toast.error("Title, Author, and Excerpt are required and cannot be empty.");
        return;
    }
    const submissionData = { 
        ...formData,
        title: formData.title.trim(),
        author: formData.author.trim(),
        excerpt: formData.excerpt.trim(),
        // content: formData.content ? formData.content.trim() : '', // If content field exists
     };

    if (isEditing) {
      updateBlogMutation.mutate(submissionData);
    } else {
      createBlogMutation.mutate(submissionData);
    }
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setBlogToDelete(null);
  };

  const confirmDelete = () => {
    if (blogToDelete) {
      deleteBlogMutation.mutate(blogToDelete._id);
    }
  };

  return (
    <> {/* Changed div to Fragment to avoid unnecessary div */}
      <style jsx global>{`
        body {
          background-color: #f8f9fa; /* Light gray background for the whole page */
        }
        .main-content-area {
          margin-left: 280px; /* Adjust to your Sidebar's width */
          padding: 2rem;    /* Increased padding */
          padding-top: 80px; /* Adjust if Topbar has a fixed height + some space */
          transition: margin-left 0.3s ease-in-out; /* Smooth transition for sidebar toggle */
        }
        @media (max-width: 992px) { /* Adjust for sidebar toggle behavior */
          .main-content-area {
            margin-left: 0;
          }
        }
        .card-header-custom {
          background-color: #007bff; /* Dark header for cards */
          color: white;
        }
        .modal-header-custom {
          background-color: #007bff; /* Primary color for modal header */
          color: white;
        }
        .modal-header-custom .btn-close {
          filter: invert(1) grayscale(100%) brightness(200%); /* White close button */
        }
        .table-hover tbody tr:hover {
            background-color: #e9ecef; /* Lighter hover for table rows */
        }
        .action-btn-group .btn {
            margin: 0 0.2rem; /* Spacing between action buttons */
        }
        .form-control:focus {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
        }
        .required-asterisk {
            color: red;
            margin-left: 2px;
        }
      `}</style>
      <Toaster position="top-right" reverseOrder={false} toastOptions={{
        style: {
          borderRadius: '8px',
          background: '#333',
          color: '#fff',
          boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
        },
        success: {
          iconTheme: {
            primary: '#28a745', // Green for success
            secondary: 'white',
          },
        },
        error: {
          iconTheme: {
            primary: '#dc3545', // Red for error
            secondary: 'white',
          },
        },
      }}/>
      <Topbar />
      <Sidebar />
      <main className="main-content-area">
        <Container fluid>
          <Row className="mb-4 align-items-center">
            <Col md={8}>
              <h1 className="h3 mb-0 text-dark font-weight-bold">Blog Management</h1>
              <p className="text-muted mb-0">Create, edit, and manage your blog posts.</p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="primary" onClick={() => handleShowModal()} className="shadow-sm py-2 px-3">
                <FiPlusCircle className="me-2" size={18}/> Create New Blog
              </Button>
            </Col>
          </Row>

          {isLoadingBlogs && (
            <div className="text-center my-5 py-5">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }}/>
              <p className="mt-3 mb-0 text-primary">Loading blogs, please wait...</p>
            </div>
          )}

          {isErrorBlogs && (
            <Alert variant="danger" className="shadow">
              <Alert.Heading>Oops! Something went wrong.</Alert.Heading>
              <p>
                Error fetching blogs: {errorBlogs?.response?.data?.message || errorBlogs?.message || "An unknown error occurred. Please try again later."}
              </p>
            </Alert>
          )}

          {!isLoadingBlogs && !isErrorBlogs && blogsData && (
            blogsData.length === 0 ? (
                <Card className="text-center shadow-sm">
                    <Card.Body className="py-5">
                        <FiFileText size={48} className="text-muted mb-3"/>
                        <h4 className="text-muted">No Blogs Found</h4>
                        <p className="text-muted">
                            It looks like there are no blog posts yet. <br/>
                            Click the "Create New Blog" button to add your first post!
                        </p>
                    </Card.Body>
                </Card>
            ) : (
            <Card className="shadow-sm">
              <Card.Header className="card-header-custom d-flex justify-content-between align-items-center">
                <h5 className="mb-0">All Blog Posts ({blogsData.length})</h5>
              </Card.Header>
              <Card.Body className="p-0"> {/* Remove padding for table to fit edges */}
                <Table striped bordered hover responsive className="align-middle mb-0">
                  <thead className="table-dark"> {/* Darker header for better contrast */}
                    <tr>
                      <th className="text-center" style={{width: '5%'}}>#</th>
                      <th style={{width: '25%'}}>Title</th>
                      <th style={{width: '15%'}}>Author</th>
                      <th style={{width: '30%'}}>Excerpt</th>
                      <th className="text-center" style={{width: '10%'}}>Date</th>
                      <th className="text-center" style={{width: '15%'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogsData.map((blog, index) => (
                      <tr key={blog._id}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <span className="fw-bold">{blog.title}</span>
                        </td>
                        <td>{blog.author}</td>
                        <td>
                            {blog.excerpt.length > 80 
                                ? `${blog.excerpt.substring(0, 80)}...` 
                                : blog.excerpt
                            }
                        </td>
                        <td className="text-center text-nowrap">
                           <Badge pill bg="light" text="dark" className="p-2">
                             <FiCalendar size={13} className="me-1"/>{formatDate(blog.date)}
                           </Badge>
                        </td>
                        <td className="text-center action-btn-group">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewBlog(blog)}
                            title="View Details"
                          >
                            <FiEye size={16}/>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(blog)}
                            title="Edit Blog"
                          >
                            <FiEdit size={16}/>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(blog)}
                            title="Delete Blog"
                          >
                            <FiTrash2 size={16}/>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
            )
          )}
        </Container>
      </main>

      {/* Blog Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} centered size="lg" className="blog-form-modal">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            {isEditing ? <><FiEdit className="me-2"/>Edit Blog Post</> : <><FiPlusCircle className="me-2"/>Create New Blog Post</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="blogTitle">
              <Form.Label>Title <span className="required-asterisk">*</span></Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="E.g., Top 10 Summer Cocktails"
                className="form-control-lg"
              />
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3" controlId="blogAuthor">
                        <Form.Label>Author <span className="required-asterisk">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            name="author"
                            
                            value={formData.author}
                            onChange={handleChange}
                            required
                            placeholder="E.g., Jane Doe"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3" controlId="blogDate">
                        <Form.Label>Publish Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-4" controlId="blogExcerpt">
              <Form.Label>Excerpt <span className="required-asterisk">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                placeholder="A short, catchy summary of your blog post..."
              />
              <Form.Text muted>
                This will be shown as a preview. Keep it concise (around 1-2 sentences).
              </Form.Text>
            </Form.Group>

            {/* // Uncomment if you add 'content' to your BlogModel and want to manage it here
            <Form.Group className="mb-3" controlId="blogContent">
              <Form.Label>Full Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={8} // Increased rows for better editing experience
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Write the full content of your blog post here..."
              />
            </Form.Group>
            */}
            <hr className="my-4"/>
            <div className="d-flex justify-content-end">
                <Button variant="outline-secondary" onClick={handleCloseModal} className="me-3 py-2 px-3">
                    Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={createBlogMutation.isLoading || updateBlogMutation.isLoading} className="py-2 px-4 shadow-sm">
                    {(createBlogMutation.isLoading || updateBlogMutation.isLoading) && (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
                    )}
                    {isEditing ? "Save Changes" : "Publish Blog"}
                </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View Blog Details Modal */}
      <Modal show={showViewModal} onHide={handleCloseViewModal} centered size="lg">
        <Modal.Header closeButton className="modal-header-custom">
            <Modal.Title><FiFileText className="me-2" />Blog Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
            {blogToView ? (
                <>
                    <h3 className="mb-3">{blogToView.title}</h3>
                    <p className="text-muted mb-1">
                        <FiUser size={16} className="me-2" /> By: <strong>{blogToView.author}</strong>
                    </p>
                    <p className="text-muted mb-3">
                        <FiCalendar size={16} className="me-2" /> Published: <strong>{formatDate(blogToView.date)}</strong>
                    </p>
                    <h5 className="mt-4">Excerpt:</h5>
                    <p className="lead bg-light p-3 rounded">{blogToView.excerpt}</p>
                    
                    {/* Display content if available in your model & data */}
                    {/* {blogToView.content && (
                        <>
                            <h5 className="mt-4">Full Content:</h5>
                            <div 
                                className="blog-content-view p-3 border rounded" 
                                style={{maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap'}}
                                dangerouslySetInnerHTML={{ __html: blogToView.content.replace(/\n/g, '<br />') }} // Basic rendering
                            />
                        </>
                    )} */}
                    {!(blogToView.content) && (
                        <p className="text-muted fst-italic mt-4">(Full content not available or not included in this view)</p>
                    )}
                </>
            ) : (
                <p>No blog details to display.</p>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseViewModal}>
                Close
            </Button>
        </Modal.Footer>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={handleCloseDeleteConfirm} centered size="md" className="delete-confirm-modal">
        <Modal.Header closeButton style={{backgroundColor: '#f8d7da', color: '#721c24', borderBottom: '1px solid #f5c6cb'}}>
          <Modal.Title><FiTrash2 className="me-2"/>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
            <FiTrash2 size={48} className="text-danger mb-3"/>
          <h5 className="mb-3">Are you absolutely sure?</h5>
          <p className="text-muted">
            This action cannot be undone. This will permanently delete the blog post titled: <br/>
            <strong className="text-dark d-block mt-2">"{blogToDelete?.title}"</strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="secondary" onClick={handleCloseDeleteConfirm} className="px-4">
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleteBlogMutation.isLoading} className="px-4">
             {deleteBlogMutation.isLoading && (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
            )}
            Delete Blog
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Blogs;
